import {
  Component, OnInit, OnChanges, SimpleChanges,
  input, output, effect, signal, ViewChild, ElementRef
} from "@angular/core";

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { ICreateThemeReq, ITheme } from "../../../../Utils/_models/themes.model";
import { GenericQuestionsService } from "../../../../Utils/_services/admin/GenericQuestions/generic-questions.service";

@Component({
  selector: "app-theme-form",
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule],
  templateUrl: "./theme-form.component.html",
  styleUrl: "./theme-form.component.css",
})
export class ThemeFormComponent implements OnInit, OnChanges {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('cropCanvas') cropCanvas!: ElementRef<HTMLCanvasElement>;

  isVisible = input(false);
  isEditMode = input(false);
  theme = input<ITheme | null>(null);
  isLoading = input(false);

  close = output<void>();
  formSubmit = output<ICreateThemeReq>();

  themeForm!: FormGroup;
  selectedFile: File | null = null;
  showImgError = signal(false);

  existingImageUrl: string | null = null;

  // Crop state
  showCropper = false;
  cropImgSrc = '';
  croppedBlob: Blob | null = null;
  croppedPreviewUrl = '';

  // Generic Questions state
  genericQuestionsList: { question: string; group: string }[] = [];
  questionGroups = signal<any[]>([]);
  isAddingQuestion = false;
  newQuestionText = '';
  newQuestionGroup = '';
  editingIndex = -1;
  editQuestionText = '';
  editQuestionGroup = '';

  private img = new Image();
  private isDragging = false;
  private isResizing = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private resizeHandle = '';
  cropBox = { x: 50, y: 50, w: 300, h: 200 };
  private canvasW = 480;
  private canvasH = 320;

  constructor(
    private fb: FormBuilder,
    private genericQuestionsService: GenericQuestionsService
  ) {
    effect(() => {
      if (this.isLoading()) { this.themeForm?.disable(); }
      else { this.themeForm?.enable(); }
    });
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadQuestionGroups();
    if (this.theme()) { this.populateForm(); }
    else { this.resetForm(); }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['theme']) {
      if (this.theme()) { this.populateForm(); }
      else { this.resetForm(); }
    }
  }

  private initializeForm(): void {
    this.themeForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.minLength(25)]],
      entryFee: [0, [Validators.required, Validators.min(0)]],  // ✅ min(0) for free lobbies
      participantsCount: [0, [Validators.required, Validators.min(2)]],
      interestsCount: [0, [Validators.required, Validators.min(1)]],
      participantsGender: ['male', Validators.required],
      isActive: [true],
      startDate: [''],
      questionGroup: [''],
      roundsPerInterest: [1, [Validators.required, Validators.min(1)]],
      genericQuestionsDuration: [30, [Validators.required, Validators.min(5), Validators.max(300)]],
    });
  }

  loadQuestionGroups(): void {
    this.genericQuestionsService.getGroups().subscribe({
      next: (res: any) => {
        if (res?.data) {
          const groups = [...res.data];
          
          const currentGroup = this.theme()?.defaults?.questionGroup;
          if (currentGroup && !groups.some((g: any) => g._id === currentGroup || g.name === currentGroup)) {
            groups.push({ _id: currentGroup, name: currentGroup });
          }

          this.questionGroups.set(groups);
        }
      },
      error: (err) => {
        console.error("Failed to load question groups", err);
      }
    });
  }

  private populateForm(): void {
    if (!this.themeForm) return;
    const themeData = this.theme();
    if (themeData) {
      this.themeForm.patchValue({
        name: themeData.name || '',
        description: themeData.description ?? '',
        isActive: themeData.isActive ?? false,
        entryFee: themeData.defaults?.entryFee ?? 0,
        participantsCount: themeData.defaults?.participantsCount ?? 0,
        interestsCount: themeData.defaults?.interestsCount ?? 0,
        participantsGender: themeData.defaults?.participantsGender || 'male',
        startDate: themeData.defaults?.startDate ? new Date(themeData.defaults.startDate).toISOString().slice(0, 16) : '',
        questionGroup: themeData.defaults?.questionGroup || '',
        roundsPerInterest: themeData.defaults?.roundsPerInterest ?? 1,
        genericQuestionsDuration: themeData.defaults?.genericQuestionsDuration ?? 30,
      });
      this.selectedFile = null;
      this.croppedBlob = null;
      this.showCropper = false;
      this.croppedPreviewUrl = themeData.image?.signedUrl ?? '';
      this.cropImgSrc = themeData.image?.signedUrl ?? '';
      this.existingImageUrl = themeData.image?.signedUrl ?? null;

      const currentGroup = themeData.defaults?.questionGroup?.trim();
      if (currentGroup && !this.questionGroups().includes(currentGroup)) {
        this.questionGroups.update(groups => Array.from(new Set([...groups, currentGroup])).sort());
      }

      // Populate genericQuestionsList
      this.genericQuestionsList = (themeData.defaults as any)?.genericQuestions?.map((q: any) => {
        if (typeof q === 'string') {
          try {
            const parsed = JSON.parse(q);
            if (parsed && typeof parsed === 'object' && parsed.question) {
              return { question: parsed.question, group: parsed.group || '' };
            }
          } catch (e) {}
          return { question: q, group: '' };
        }
        return { question: q.question || '', group: q.group || '' };
      }) || [];

      // preload with crossOrigin, fallback if CORS blocked
      this.img = new Image();
      this.img.crossOrigin = 'anonymous';
      this.img.onerror = () => {
        this.img = new Image();
        this.img.src = this.cropImgSrc;
      };
      this.img.src = this.cropImgSrc;
    }
  }

  private resetForm(): void {
    if (this.themeForm) {
      this.themeForm.reset({
        name: '', description: '', entryFee: 0,
        interestsCount: 0, participantsCount: 0,
        participantsGender: 'male', isActive: true,
        questionGroup: '', roundsPerInterest: 1,
        genericQuestionsDuration: 30,
      });
      this.selectedFile = null;
      this.croppedBlob = null;
      this.croppedPreviewUrl = '';
      this.cropImgSrc = '';
      this.existingImageUrl = null;
      this.showCropper = false;
      this.showImgError.set(false);
      this.genericQuestionsList = [];
      this.isAddingQuestion = false;
      this.editingIndex = -1;
    }
  }

  removeImage(): void {
    this.croppedPreviewUrl = '';
    this.croppedBlob = null;
    this.cropImgSrc = '';
    this.existingImageUrl = null;
    this.selectedFile = null;
    this.showCropper = false;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  reCrop(): void {
    this.croppedPreviewUrl = '';
    this.croppedBlob = null;
    this.selectedFile = null;
    this.showCropper = true;
    setTimeout(() => {
      const canvas = this.cropCanvas?.nativeElement;
      if (!canvas) return;
      this.canvasW = canvas.width;
      this.canvasH = canvas.height;
      const w = this.canvasW * 0.75;
      const h = w * 0.6;
      this.cropBox = { x: (this.canvasW - w) / 2, y: (this.canvasH - h) / 2, w, h };

      // always force fresh reload — never rely on img.complete
      this.img = new Image();
      this.img.crossOrigin = 'anonymous';
      this.img.onload = () => this.drawCropper();
      this.img.onerror = () => {
        // CORS blocked — reload without crossOrigin (canvas will be tainted but visible)
        this.img = new Image();
        this.img.onload = () => this.drawCropper();
        this.img.src = this.cropImgSrc + '?t=' + Date.now(); // cache bust
      };
      this.img.src = this.cropImgSrc;
    }, 50);
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.cropImgSrc = e.target?.result as string;
      this.showCropper = true;
      this.croppedPreviewUrl = '';
      this.croppedBlob = null;
      this.showImgError.set(false);
      setTimeout(() => this.initCropper(), 50);
    };
    reader.readAsDataURL(input.files[0]);
  }

  onSubmit(): void {
    this.themeForm.markAllAsTouched();
    const noImage = !this.croppedBlob && !this.existingImageUrl;
    this.showImgError.set(noImage);
    if (this.themeForm.invalid || this.showImgError()) return;
    const formValue = this.themeForm.value;
    this.formSubmit.emit({
      name: formValue.name.trim(),
      description: formValue.description?.trim(),
      isActive: formValue.isActive,
      image: this.selectedFile,
      entryFee: formValue.entryFee,
      participantsCount: formValue.participantsCount,
      participantsGender: formValue.participantsGender,
      interestsCount: formValue.interestsCount,
      startDate: formValue.startDate || null,
      questionGroup: formValue.questionGroup || null,
      roundsPerInterest: formValue.roundsPerInterest || null,
      genericQuestions: this.genericQuestionsList.map(q => JSON.stringify(q)),
      genericQuestionsDuration: formValue.genericQuestionsDuration,
    });
  }

  // ── Image / Crop ──────────────────────────────────────────

  triggerFileInput(): void { this.fileInput.nativeElement.click(); }

  openImageInNewTab(url: string | null): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  private initCropper(): void {
    const canvas = this.cropCanvas?.nativeElement;
    if (!canvas) return;
    this.img = new Image();
    this.img.onload = () => {
      this.canvasW = canvas.width;
      this.canvasH = canvas.height;
      const w = this.canvasW * 0.75;
      const h = w * 0.6; // rectangle aspect ~5:3
      this.cropBox = {
        x: (this.canvasW - w) / 2,
        y: (this.canvasH - h) / 2,
        w, h,
      };
      this.drawCropper();
    };
    this.img.src = this.cropImgSrc;
  }

  private drawCropper(): void {
    const canvas = this.cropCanvas?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const { x, y, w, h } = this.cropBox;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(this.img, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Cut out rectangle crop area
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.drawImage(this.img, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);

    // Corner handles
    const hs = 10;
    ctx.fillStyle = '#fff';
    [{ cx: x, cy: y }, { cx: x + w, cy: y }, { cx: x, cy: y + h }, { cx: x + w, cy: y + h }]
      .forEach(c => ctx.fillRect(c.cx - hs / 2, c.cy - hs / 2, hs, hs));
  }

  onCanvasMouseDown(event: MouseEvent): void {
    const pos = this.getCanvasPos(event);
    const handle = this.getResizeHandle(pos);
    if (handle) { this.isResizing = true; this.resizeHandle = handle; }
    else if (this.inCropBox(pos)) { this.isDragging = true; }
    this.dragStartX = pos.x;
    this.dragStartY = pos.y;
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.isDragging && !this.isResizing) return;
    const pos = this.getCanvasPos(event);
    const dx = pos.x - this.dragStartX;
    const dy = pos.y - this.dragStartY;
    this.dragStartX = pos.x;
    this.dragStartY = pos.y;
    const b = this.cropBox;
    const minSize = 40;
    if (this.isDragging) {
      b.x = Math.max(0, Math.min(b.x + dx, this.canvasW - b.w));
      b.y = Math.max(0, Math.min(b.y + dy, this.canvasH - b.h));
    } else if (this.isResizing) {
      if (this.resizeHandle.includes('e')) b.w = Math.max(minSize, b.w + dx);
      if (this.resizeHandle.includes('s')) b.h = Math.max(minSize, b.h + dy);
      if (this.resizeHandle.includes('w')) { b.w = Math.max(minSize, b.w - dx); b.x += dx; }
      if (this.resizeHandle.includes('n')) { b.h = Math.max(minSize, b.h - dy); b.y += dy; }
    }
    this.drawCropper();
  }

  onCanvasMouseUp(): void { this.isDragging = false; this.isResizing = false; }

  private getCanvasPos(event: MouseEvent): { x: number; y: number } {
    const rect = this.cropCanvas.nativeElement.getBoundingClientRect();
    const scaleX = this.cropCanvas.nativeElement.width / rect.width;
    const scaleY = this.cropCanvas.nativeElement.height / rect.height;
    return { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY };
  }

  private inCropBox(pos: { x: number; y: number }): boolean {
    const { x, y, w, h } = this.cropBox;
    return pos.x >= x && pos.x <= x + w && pos.y >= y && pos.y <= y + h;
  }

  private getResizeHandle(pos: { x: number; y: number }): string {
    const { x, y, w, h } = this.cropBox;
    const t = 12;
    const near = (a: number, b: number) => Math.abs(a - b) < t;
    let handle = '';
    if (near(pos.y, y)) handle += 'n';
    if (near(pos.y, y + h)) handle += 's';
    if (near(pos.x, x)) handle += 'w';
    if (near(pos.x, x + w)) handle += 'e';
    return handle;
  }

  applyCrop(): void {
    const { x, y, w, h } = this.cropBox;
    const scaleX = this.img.naturalWidth / this.canvasW;
    const scaleY = this.img.naturalHeight / this.canvasH;
    const offscreen = document.createElement('canvas');
    offscreen.width = Math.round(w * scaleX);
    offscreen.height = Math.round(h * scaleY);
    const ctx = offscreen.getContext('2d')!;
    ctx.drawImage(this.img, Math.round(x * scaleX), Math.round(y * scaleY),
      Math.round(w * scaleX), Math.round(h * scaleY), 0, 0, offscreen.width, offscreen.height);
    offscreen.toBlob((blob) => {
      if (!blob) return;
      this.croppedBlob = blob;
      this.selectedFile = new File([blob], 'theme-image.jpg', { type: 'image/jpeg' });
      this.croppedPreviewUrl = URL.createObjectURL(blob);
      this.showCropper = false;
    }, 'image/jpeg', 0.85);
  }





  undoClearedImage(): void {
    if (this.isEditMode() && this.theme()?.image?.signedUrl) {
      this.croppedPreviewUrl = this.theme()!.image!.signedUrl!;
      this.cropImgSrc = this.theme()!.image!.signedUrl!;
      this.existingImageUrl = this.theme()!.image!.signedUrl!;
      this.selectedFile = null;
      this.croppedBlob = null;
      this.showCropper = false;
      this.showImgError.set(false);
    }
  }
  getFileName(url: string | undefined): string {
    if (!url) return 'No image';
    return url.split('/').pop() || 'image file';
  }



  onClose(): void { this.close.emit(); }

  get modalTitle(): string { return this.isEditMode() ? 'Edit Theme' : 'Create New Theme'; }
  get submitButtonText(): string {
    return this.isLoading() ? 'Saving...' : this.isEditMode() ? 'Update Theme' : 'Create Theme';
  }
  isFormGroupInvalid(controlName: string): boolean {
    const control = this.themeForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  // Generic Questions Actions
  startAddQuestion(): void {
    this.isAddingQuestion = true;
    this.newQuestionText = '';
    this.newQuestionGroup = '';
  }

  cancelAddQuestion(): void {
    this.isAddingQuestion = false;
  }

  saveNewQuestion(): void {
    if (this.newQuestionText.trim()) {
      this.genericQuestionsList.push({
        question: this.newQuestionText.trim(),
        group: this.newQuestionGroup.trim()
      });
      this.isAddingQuestion = false;
    }
  }

  startEditQuestion(index: number): void {
    this.editingIndex = index;
    this.editQuestionText = this.genericQuestionsList[index].question;
    this.editQuestionGroup = this.genericQuestionsList[index].group;
  }

  cancelQuestionEdit(): void {
    this.editingIndex = -1;
  }

  saveQuestionEdit(index: number): void {
    if (this.editQuestionText.trim()) {
      this.genericQuestionsList[index] = {
        question: this.editQuestionText.trim(),
        group: this.editQuestionGroup.trim()
      };
      this.editingIndex = -1;
    }
  }

  deleteQuestion(index: number): void {
    this.genericQuestionsList.splice(index, 1);
  }

}